import * as React from 'react'
import { X } from 'lucide-react'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@Components/ui/carousel'
import { Pagination } from '@Components/pagination'
import { BannerCardResponse } from '../../apis/product/productBusinessPages'
import Image from 'next/image'
import { API_URL } from '../../apis/constants'
import { IconButton } from '@Components/iconButton'
import Typography from '@Components/typo'

export function CarouselContainer({
  pageCount,
  children,
  variant,
  customClass,
  isShowPagination = true,
  dataFullPage,
  dataLength,
  isFullPage = false,
  onCloseModal,
}: {
  pageCount: number
  dataLength: number
  children?: any
  variant?: 'default' | 'inverse'
  customClass?: string
  isShowPagination?: boolean
  dataFullPage?: BannerCardResponse[]
  isFullPage?: boolean
  defaultFullPage?: number
  onClick?: any
  onCloseModal?: any
}) {
  const [api, setApi] = React.useState<CarouselApi>()
  const [apiModal, setApiModal] = React.useState<CarouselApi>()
  const [current, setCurrent] = React.useState(0)
  const [currentModal, setCurrentModal] = React.useState(0)
  const [count, setCount] = React.useState(pageCount)
  const [countModal, setCountModal] = React.useState(pageCount)
  const [showNextPreviousButton, setShowNextPreviousButton] =
    React.useState(false)

  React.useEffect(() => {
    if (!api) {
      return
    }

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap() + 1)
    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1)
    })
  }, [api])

  React.useEffect(() => {
    if (!apiModal) {
      return
    }
    setCountModal(apiModal.scrollSnapList().length)
    setCurrentModal(apiModal.selectedScrollSnap() + 1)
    apiModal.on('select', () => {
      setCurrentModal(apiModal.selectedScrollSnap() + 1)
    })
  }, [apiModal])

  return (
    <div className='h-full'>
      <div
        className={`w-full ${customClass}`}
        onMouseEnter={() => setShowNextPreviousButton(true)}
        onMouseLeave={() => setShowNextPreviousButton(false)}
      >
        <Carousel setApi={setApi} className='w-full'>
          <CarouselContent>{children}</CarouselContent>
          {showNextPreviousButton && current > 1 && current <= dataLength && (
            <CarouselPrevious variant='dark' />
          )}
          {showNextPreviousButton && current < dataLength && (
            <CarouselNext variant='dark' />
          )}
        </Carousel>
        {isShowPagination && (
          <div className='text-muted-foreground align-center flex flex-row justify-center py-2 text-center text-sm'>
            {count > 1 &&
              Array.from({ length: count }).map((_, index) => (
                <Pagination
                  key={`carousel-${index}`}
                  variant={variant}
                  isActive={current === index + 1}
                  onClick={() => {
                    api.scrollTo(index)
                    setCurrent(index + 1)
                  }}
                />
              ))}
          </div>
        )}
      </div>
      {isFullPage && dataFullPage && onCloseModal && (
        <div className='fixed left-0 top-0 z-[60] flex flex h-screen w-full justify-center bg-content-default'>
          <div className='flex w-full flex-col'>
            <div className='flex w-full justify-end p-4'>
              <IconButton onClick={onCloseModal}>
                <X className='h-icon-lg w-icon-lg' />
              </IconButton>
            </div>
            <Carousel
              setApi={setApiModal}
              className='flex h-[calc(100%_-_11rem)] w-full items-center'
            >
              <CarouselContent>
                {Array.from({
                  length: dataFullPage.length,
                }).map((_, index) => (
                  <CarouselItem
                    key={index}
                    className='flex flex-col items-center justify-between gap-8'
                  >
                    <div className='w-full'>
                      <Image
                        width={100}
                        height={100}
                        className='max-h-[550px] w-full object-cover'
                        src={`${API_URL}${dataFullPage[index].Image.url}`}
                        alt=''
                      />
                    </div>
                    <div className='flex flex-col items-center justify-center gap-2 pb-4 text-white'>
                      <Typography weight='bold' color='white'>
                        {dataFullPage[index].Title}
                      </Typography>
                      <Typography size='small' color='white'>
                        {dataFullPage[index].SubTitle}
                      </Typography>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {currentModal > 1 && currentModal <= dataLength && (
                <CarouselPrevious variant='dark' />
              )}
              {currentModal < dataLength && <CarouselNext variant='dark' />}
            </Carousel>
            <div className='bottom-0 flex h-[48px] flex-row justify-center py-2 text-center text-sm'>
              {countModal > 1 &&
                Array.from({ length: countModal }).map((_, index) => (
                  <Pagination
                    key={`carousel-modal-${index}`}
                    variant='inverse'
                    isActive={currentModal === index + 1}
                    onClick={() => {
                      apiModal.scrollTo(index)
                      setCurrentModal(index + 1)
                    }}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
